import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { DefaultDatasetFiltersContext } from 'contextProviders/SplashScreenProviders/DatasetProviders/DefaultDatasetFiltersProvider';
import CardContext from 'contexts/CardContext';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import useFilterInput from 'hooks/useFilterInput';

// Relative: a bare 'constants' would be Node's built-in module
import { DRIVER_BONUS_DATA_SETS } from '../../constants';

// The fields and filters a records grid searches with: the page's filters,
// quick filters and the dataset's default filter. Shared by the AG datasource
// and DataTable's useInfiniteRows.
const useGridFilterInput = ({ baseView }: { baseView: FleetOps.BaseView }) => {
  const { chartDefinition } = useContext(CardContext);
  const { defaultDatasetFiltersLookup } = useContext(
    DefaultDatasetFiltersContext,
  );
  const { quickFilters, isDefaultFiltersDisabled } =
    useContext(FilterPlatesContext);
  const getSearchFields = useCallback(() => {
    if (!chartDefinition) {
      return undefined;
    }

    const newFields = Object.keys(baseView.fields);
    if (DRIVER_BONUS_DATA_SETS.includes(baseView.type)) {
      // We don't know what the full set of fields we require
      // to driver the StatusFlagCall is as fields will be defined
      // in the response expectations field.
      // For these grids, just get all fields.
      return undefined;
    }

    return newFields;
  }, [baseView.fields, baseView.type, chartDefinition]);

  const [includeFields, setIncludeFields] = useState<string[] | undefined>(() =>
    getSearchFields(),
  );

  useEffect(() => {
    setIncludeFields(getSearchFields());
  }, [getSearchFields]);

  const { filterInput: baseFilterInput } = useFilterInput({});
  const filterInput = useMemo((): FilterInput[] => {
    const result: FilterInput[] = [baseFilterInput, ...quickFilters];
    if (!isDefaultFiltersDisabled) {
      const f = defaultDatasetFiltersLookup[baseView.type];
      if (f) {
        result.push(f.filterInput);
      }
    }

    return result;
  }, [
    baseView.type,
    defaultDatasetFiltersLookup,
    baseFilterInput,
    isDefaultFiltersDisabled,
    quickFilters,
  ]);

  return { includeFields, filterInput };
};

export default useGridFilterInput;
